import { randomUUID } from 'node:crypto';
import { Router } from 'express';
import { z } from 'zod';
import { requireAdmin, requireAuth, type AuthenticatedRequest } from './auth';
import { pool } from './db';
import { decryptSensitive } from './privacy';

export const guardianChangesRouter = Router();

const guardianChangeSchema = z.object({
  reason: z.string().trim().min(10).max(1000),
});

guardianChangesRouter.post('/me/guardian-change', requireAuth,
  async (request: AuthenticatedRequest, response) => {
    if (request.authUser?.role !== 'user' || request.authUser.accountType !== 'candidate') {
      response.status(403).json({ error: 'Candidate account required' }); return;
    }
    const parsed = guardianChangeSchema.safeParse(request.body);
    if (!parsed.success) {
      response.status(400).json({ error: 'A guardian change reason is required' }); return;
    }
    const client = await pool.connect();
    try {
      await client.query('BEGIN');
      await client.query('SELECT pg_advisory_xact_lock(hashtext($1))',
        [`guardian-change:${request.authUser.id}`]);
      const active = await client.query<{
        invitation_id: string; guardian_id: string; phone_encrypted: string | null;
        guardian_account_type: 'candidate' | 'guardian';
        candidate_name: string; candidate_email: string | null; candidate_username: string | null;
        guardian_name: string; guardian_email: string | null; guardian_username: string | null;
      }>(
        `SELECT invitation.id AS invitation_id,guardian.id AS guardian_id,
                invitation.phone_encrypted,
                candidate.display_name AS candidate_name,candidate.email AS candidate_email,
                candidate.username AS candidate_username,
                guardian.display_name AS guardian_name,guardian.email AS guardian_email,
                guardian.username AS guardian_username,guardian.account_type AS guardian_account_type
         FROM guardian_invitations invitation
         JOIN users candidate ON candidate.id=invitation.candidate_user_id
         JOIN users guardian ON guardian.id=invitation.accepted_by
         WHERE invitation.candidate_user_id=$1 AND invitation.status='verified'
           AND guardian.account_type IN ('candidate','guardian')
         ORDER BY invitation.created_at DESC LIMIT 1
         FOR UPDATE OF invitation,candidate,guardian`,
        [request.authUser.id],
      );
      const link = active.rows[0];
      if (!link) {
        await client.query('ROLLBACK');
        response.status(409).json({ error: 'No verified guardian relationship is available' }); return;
      }
      const historyId = randomUUID();
      await client.query(
        `INSERT INTO guardian_change_history
           (id,candidate_user_id,candidate_name,candidate_email,candidate_username,
            former_guardian_user_id,former_guardian_name,former_guardian_email,
            former_guardian_username,former_guardian_phone_encrypted,reason)
         VALUES ($1,$2,$3,$4,$5,$6,$7,$8,$9,$10,$11)`,
        [historyId, request.authUser.id, link.candidate_name, link.candidate_email,
          link.candidate_username, link.guardian_id, link.guardian_name, link.guardian_email,
          link.guardian_username, link.phone_encrypted, parsed.data.reason],
      );
      await client.query(
        'DELETE FROM guardian_contact_matches WHERE invitation_id=$1',
        [link.invitation_id],
      );
      await client.query(
        'DELETE FROM guardian_contact_hashes WHERE invitation_id=$1',
        [link.invitation_id],
      );
      await client.query(
        `UPDATE guardian_invitations SET status='revoked',completed_at=NOW()
         WHERE id=$1`,
        [link.invitation_id],
      );
      await client.query(
        `UPDATE journey_memberships SET is_active=FALSE
         WHERE user_id=$1 AND journey_id IN (
           SELECT journey_id FROM journey_call_guardians
           WHERE participant_user_id=$2 AND guardian_user_id=$1
         )`, [link.guardian_id, request.authUser.id],
      );
      await client.query(
        `DELETE FROM journey_call_guardians
         WHERE participant_user_id=$1 AND guardian_user_id=$2`,
        [request.authUser.id, link.guardian_id],
      );
      await client.query(
        `UPDATE guardian_qr_challenges
         SET status='expired',claim_hash=NULL,claim_expires_at=NULL
         WHERE candidate_user_id=$1 AND status IN ('issued','claimed')`,
        [request.authUser.id],
      );
      if (link.guardian_account_type === 'guardian') {
        await client.query('DELETE FROM users WHERE id=$1 AND account_type=$2',
          [link.guardian_id, 'guardian']);
      }
      await client.query(
        "INSERT INTO audit_events (user_id,event_type,details) VALUES ($1,'guardian_changed',$2::jsonb)",
        [request.authUser.id, JSON.stringify({ historyId, formerGuardianUserId: link.guardian_id,
          invitationId: link.invitation_id })],
      );
      await client.query('COMMIT');
      response.json({ changed: true, historyId });
    } catch (error) {
      await client.query('ROLLBACK'); throw error;
    } finally { client.release(); }
  });

guardianChangesRouter.get('/admin/guardian-changes', requireAuth, requireAdmin,
  async (_request, response) => {
    const result = await pool.query(
      `SELECT id,candidate_user_id AS "candidateUserId",candidate_name AS "candidateName",
              candidate_email AS "candidateEmail",candidate_username AS "candidateUsername",
              former_guardian_user_id AS "formerGuardianUserId",
              former_guardian_name AS "formerGuardianName",
              former_guardian_email AS "formerGuardianEmail",
              former_guardian_username AS "formerGuardianUsername",
              former_guardian_phone_encrypted AS "formerGuardianPhoneEncrypted",
              reason,created_at AS "createdAt"
       FROM guardian_change_history ORDER BY created_at DESC LIMIT 200`,
    );
    response.json({ changes: result.rows.map(({ formerGuardianPhoneEncrypted, ...item }) => ({
      ...item,
      formerGuardianPhone: formerGuardianPhoneEncrypted
        ? decryptSensitive(formerGuardianPhoneEncrypted) : null,
    })) });
  });
